import { Location } from 'history';
import { reactFilter, TextLike } from 'mk/bazaar/common/filters/utils';
import { ACTIVE_SERVER_ID } from 'mk/settings';
import { CommentEntity } from 'mk2/apps/blogs/schemas';
import {
    COMMENTS_PER_POST_DETAIL_PAGE,
    CONTESTS_TOPIC_ID,
    COUNSELLING_CATEGORY_ID,
    UNCATEGORIZED_TOPIC_ID,
} from 'mk2/apps/forum/constants';
import { PageRef, PostEntity } from 'mk2/apps/forum/schemas';
import { PhotoEntity } from 'mk2/schemas';
import { parse } from 'query-string';
import React from 'react';

export function getPageNumberInPost(comment: CommentEntity): number {
    return (
        Math.floor(comment.positionInPost / COMMENTS_PER_POST_DETAIL_PAGE) +
        (comment.positionInPost % COMMENTS_PER_POST_DETAIL_PAGE !== 0 ? 1 : 0)
    );
}

export function parsePageNumber(location: Location): PageRef {
    const query = parse(location.search);

    let pageRef: PageRef = null;
    if (query.page === 'last') {
        pageRef = 'last';
    } else if (!query.page) { // not in query string, that means page=1
        pageRef = 1;
    } else {
        const pageNumOrNan = parseInt(query.page as string, 10);
        if (!isNaN(pageNumOrNan) && pageNumOrNan >= 1) {
            pageRef = pageNumOrNan;
        }
    }

    return pageRef;
}

export function largestAvailablePhotoVersionOfPost(photo: PhotoEntity, wishedVersion: 's600x600' | 's1600x1600') {
    // pre MessagePhoto su tieto kombinacie velkosti obrazkov:
    // '320,s150x150', '600,s150x150', '1600,600,s150x150'
    if (photo.id < {201: 13727898, 202: 13728130}[ACTIVE_SERVER_ID]) {
        // pre id-cka [0, 13728130) sa ukladala max velkost s320xn
        return 's320xn';
    } else if (photo.id < {201: 167737681, 202: 217293940}[ACTIVE_SERVER_ID]) {
        // pre id-cka [13728130, 217293940) sa vytvarala velkost s1600x1600 ak aspon jeden
        // z rozmerov obrazka bol 800px, inak velkost s600x600
        if (photo.height < 800 && photo.width < 800) {
            return 's600x600';
        } else {
            return wishedVersion === 's1600x1600' ? 's1600x1600' : 's600x600';
        }
    } else {
        // pre id-cka [217293940, ...) sa vzdy ukladal format s1600x1600
        return wishedVersion === 's1600x1600' ? 's1600x1600' : 's600x600';
    }
}

const linkProcessor = (lnk, visibleText): React.ReactChild => {
    return (
        <a href={lnk}>
            {visibleText}
        </a>
    );
};

type LnkProcessorFunction = (srcLnk: string, visibleText: string) => React.ReactChild;

const FORUM_LINK_RE = /\[\[\s*(.*?)\s*\|\s*(.*?)\s*\]\]/;

// process mediawiki format links in the form of [[easygo-virage|EasyGo Virage]]
// using provided function which takes srcLnk and visibleText parameters and
// is expected to return React elements, e.g.:
// <Link to="/kocikopedia/easygo-virage/">EasyGo Virage</Link>
// this filter is used for our INTERNAL links
const forumLinkize = (text: TextLike, lnkProcessor: LnkProcessorFunction) => reactFilter(text, (s: string): React.ReactChild[] => {
    const ret = [];
    const matcher = new RegExp(FORUM_LINK_RE, 'g');
    let from = 0;
    while (true) {
        const match = matcher.exec(s);
        if (match) {
            const matchLen = match[0].length;
            const lnk = match[1];
            const visibleText = match[2];

            const to = matcher.lastIndex - matchLen;

            // copy text before this link
            ret.push(s.substring(from, to));

            const finalLnk = lnkProcessor(lnk, visibleText);
            ret.push(finalLnk);

            from = matcher.lastIndex;
        } else {
            // copy text after last link
            ret.push(s.substring(from));
            break;
        }
    }
    return ret;
});

export const linkize = (text: TextLike) => forumLinkize(text, (lnk, visibleText) => linkProcessor(lnk, visibleText));

export const isContestPost = (post: PostEntity): boolean =>
    post && post.topic && post.topic.id === CONTESTS_TOPIC_ID;

export const isUncategorizedPost = (post: PostEntity): boolean =>
    post && post.topic && post.topic.id === UNCATEGORIZED_TOPIC_ID;

export const isCounsellingPost = (post: PostEntity): boolean =>
    post && post.topic.category && post.topic.category.id === COUNSELLING_CATEGORY_ID;
